import axios from 'axios';
import {
    PRIMARY_MAXPARK_FUNCTION_ORIGIN,
    getFunctionUrl,
    getRuntimeHostname,
} from '../../../utils/apiHost';

export const PUBLIC_PAYMENT_API_ORIGIN = PRIMARY_MAXPARK_FUNCTION_ORIGIN;
export const CANONICAL_TICKET_LOOKUP_URL = `${PUBLIC_PAYMENT_API_ORIGIN}/ticket-lookup`;
export const DEFAULT_LOOKUP_RETRY_COUNT = 0;
export const DEFAULT_LOOKUP_RETRY_DELAY_MS = 350;
export const DEFAULT_LOOKUP_TIMEOUT_MS = 8000;

const notFoundStatuses = new Set([404]);

export const getPrimaryTicketLookupUrl = ({
    env = process.env.NODE_ENV,
    hostname = getRuntimeHostname(),
    serverApi,
} = {}) => {
    return getFunctionUrl('ticket-lookup', { env, hostname, serverApi });
};

export const shouldUseCanonicalLookupFallback = (
    primaryUrl,
    {
        env = process.env.NODE_ENV,
        hostname = getRuntimeHostname(),
    } = {}
) => {
    if (!primaryUrl || primaryUrl === CANONICAL_TICKET_LOOKUP_URL) {
        return false;
    }

    return env === 'production' || /^www?\.maxparksolutions\.(com|ca)$/i.test(hostname);
};

export const getTicketLookupUrls = (options = {}) => {
    const primaryUrl = getPrimaryTicketLookupUrl(options);
    const urls = [primaryUrl];

    if (shouldUseCanonicalLookupFallback(primaryUrl, options)) {
        urls.push(CANONICAL_TICKET_LOOKUP_URL);
    }

    return urls.filter((url, index) => url && urls.indexOf(url) === index);
};

// Turnstile tokens are single-use. A retry must start from a fresh user challenge,
// not resend the same token to another URL after the server has consumed it.
export const findTicketWithPaymentApi = async (ticketId, options = {}) => {
    const axiosClient = options.axiosClient || axios;
    const urls = options.urls || getTicketLookupUrls(options);
    const timeoutMs = Number.isFinite(options.timeoutMs) ? Math.max(0, options.timeoutMs) : DEFAULT_LOOKUP_TIMEOUT_MS;
    const unavailable = { handled: true, unavailable: true, paymentAccess: null };
    if (!urls[0]) return unavailable;
    try {
        const response = await axiosClient.post(urls[0], {
            id: ticketId, turnstileToken: String(options.turnstileToken || '').trim(),
        }, {
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, timeout: timeoutMs,
        });
        const data = response.data || {};
        if (data.status === 'ok' && data.ticketNumber && data.currency === 'CAD' && typeof data.payable === 'boolean') {
            return { handled: true, paymentAccess: data };
        }
        return data.status === 'not_found' ? { handled: true, paymentAccess: null } : unavailable;
    } catch (error) {
        const status = error.response?.status;
        if (notFoundStatuses.has(status)) return { handled: true, paymentAccess: null };
        console.warn('Server ticket lookup failed.', { status: Number.isInteger(status) ? status : 'unknown' });
        return unavailable;
    }
};